import { Empty, Table, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import moment from "moment"
import { StockMovementData } from "services/interface"

const INFLOW_TYPES = ["in", "transfer_in", "unreserved"]

const isAdjustmentMovement = (record: StockMovementData) =>
  record.movement_type === "adjustment" ||
  !!record.remark?.toLowerCase().includes("stock adjustment")

const isInflowMovement = (record: StockMovementData) => {
  if (isAdjustmentMovement(record)) {
    return !!record.remark?.toUpperCase().includes("(IN)")
  }
  return INFLOW_TYPES.includes(record.movement_type)
}

// ตัด prefix "Receive: " / "Issue: " ออกจาก remark (เหมือน TransferTab ฝั่ง admin)
const extractCodeFromRemark = (remark: string | null | undefined): string => {
  if (!remark) return "-"
  if (remark.toLowerCase().includes("stock adjustment")) return ""
  return remark.replace(/^(Receive|Issue):\s*/i, "").trim() || "-"
}

const formatDateTime = (dateString: string | null | undefined) => {
  if (!dateString) return "-"
  return moment.utc(dateString).format("DD/MM/YYYY, HH:mm")
}

interface MovementTableProps {
  data: StockMovementData[]
  loading?: boolean
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
}

const MovementTable = ({
  data,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
}: MovementTableProps) => {
  if (!loading && (!data || data.length === 0)) {
    return (
      <Empty
        description="ไม่พบข้อมูลการโอนสินค้า"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        style={{ padding: 24 }}
      />
    )
  }

  const columns: ColumnsType<StockMovementData> = [
    {
      title: "เลขที่รับ/ส่ง",
      dataIndex: "remark",
      key: "code",
      width: 150,
      render: (_: unknown, record: StockMovementData) =>
        extractCodeFromRemark(record.remark),
    },
    {
      title: "ประเภท",
      dataIndex: "movement_type",
      key: "movement_type",
      width: 100,
      render: (_: unknown, record: StockMovementData) => {
        const isAdjustment = isAdjustmentMovement(record)
        const isIn = isInflowMovement(record)
        return (
          <Typography.Text strong type={isIn ? "success" : "danger"}>
            {isAdjustment ? "ปรับสต็อก" : isIn ? "รับเข้า" : "ส่งออก"}
          </Typography.Text>
        )
      },
    },
    {
      title: "สินค้า",
      dataIndex: "product",
      key: "product",
      width: 200,
      render: (_: unknown, record: StockMovementData) =>
        record.product?.name || record.product_id || "-",
    },
    {
      title: "ตัวเลือกสินค้า SKU",
      dataIndex: "product_sku",
      key: "product_sku",
      width: 200,
      render: (_: unknown, record: StockMovementData) =>
        record.product_sku?.warehouse_sku_name || record.product_sku_id || "-",
    },
    {
      title: "คลังต้นทาง",
      dataIndex: "warehouse_from",
      key: "warehouse_from",
      width: 180,
      render: (_: unknown, record: StockMovementData) =>
        record.warehouse_from?.name || record.warehouse?.name || "-",
    },
    {
      title: "คลังปลายทาง",
      dataIndex: "warehouse_to",
      key: "warehouse_to",
      width: 180,
      render: (_: unknown, record: StockMovementData) =>
        record.warehouse_to?.name || "-",
    },
    {
      title: "จำนวนก่อน",
      dataIndex: "qty_before",
      key: "qty_before",
      align: "right" as const,
      width: 120,
      render: (value: number | null) => (value ?? 0).toLocaleString(),
    },
    {
      title: "จำนวนรับ/ส่ง",
      dataIndex: "qty",
      key: "qty",
      align: "right" as const,
      width: 110,
      render: (value: number, record: StockMovementData) => {
        const isAdjustment = isAdjustmentMovement(record)
        const isIn = isInflowMovement(record)
        const sign = isAdjustment ? (isIn ? "+" : "-") : isIn ? "+" : ""
        return (
          <Typography.Text type={isIn ? "success" : "danger"}>
            {sign} {Math.abs(value ?? 0).toLocaleString()}
          </Typography.Text>
        )
      },
    },
    {
      title: "จำนวนคงเหลือ",
      dataIndex: "qty_after",
      key: "qty_after",
      align: "right" as const,
      width: 120,
      render: (value: number | null) => (value ?? 0).toLocaleString(),
    },
    {
      title: "วันที่รับ/ส่งสินค้า",
      dataIndex: "movement_date",
      key: "movement_date",
      width: 140,
      render: (value: string) => formatDateTime(value),
    },
    {
      title: "ผู้รับสินค้า/ผู้ส่งสินค้า",
      dataIndex: "create_user",
      key: "create_user",
      width: 150,
      render: (_: unknown, record: StockMovementData) =>
        record.create_user
          ? `${record.create_user.first_name || ""} ${
              record.create_user.last_name || ""
            }`.trim() || "-"
          : "-",
    },
    {
      title: "หมายเหตุ",
      dataIndex: "remark",
      key: "remark",
      width: 200,
      render: (value: string | null) =>
        value || <Typography.Text type="secondary">-</Typography.Text>,
    },
  ]

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      size="small"
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: false,
        onChange: onPageChange,
      }}
      scroll={{ x: 1200 }}
    />
  )
}

export default MovementTable
